import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as ExtractionRuleActions from './extraction-rule.actions';
import { TableNames } from '../../services/utils/dexie.utils';
import { DexieService } from '../../services/dexie.service';
import { selectExtractionRules } from './extraction-rule.selectors';
import { ExtractionRuleService } from '../../services/extraction-rule.service';
import { ExtractionRule } from '../../services/model';

@Injectable({
  providedIn: 'root',
})
export class ExtractionRuleEffects {
  loadExtractionRules$;
  loadExtractionRule$;
  createExtractionRule$;
  updateExtractionRule$;
  deleteExtractionRule$;
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly extractionRuleService: ExtractionRuleService,
    private readonly dexieService: DexieService
  ) {
    this.loadExtractionRules$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.fetchExtractionRules),
        switchMap(() =>
          this.dexieService.allItems<ExtractionRule>(TableNames.EXTRACTION_RULE).pipe(
            catchError(() => of([]))
          )
        ),
        tap((extractionRules) => {
          if (extractionRules.length > 0) {
            this.store.dispatch(ExtractionRuleActions.loadExtractionRules({ extractionRules }));
          }
        }),
        switchMap(() => this.extractionRuleService.getAll()),
        switchMap((extractionRules) =>
          this.dexieService.updateTable(TableNames.EXTRACTION_RULE, extractionRules).pipe(
            map(() => extractionRules),
            catchError(() => of(extractionRules))
          )
        ),
        map((extractionRules) => ExtractionRuleActions.loadExtractionRules({ extractionRules })),
        catchError((error) => {
          console.error('Error loading extraction rules:', error);
          return of(ExtractionRuleActions.loadExtractionRules({ extractionRules: [] }));
        })
      )
    );

    this.loadExtractionRule$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.fetchExtractionRule),
        switchMap(({ id }) =>
          this.dexieService.itemById<ExtractionRule>(TableNames.EXTRACTION_RULE, id).pipe(
            withLatestFrom(this.store.select(selectExtractionRules)),
            tap(([rule, extractionRules]) => {
              if (rule) {
                const oldRules = extractionRules.filter((r) => r.id !== rule.id);
                this.store.dispatch(
                  ExtractionRuleActions.loadExtractionRules({ extractionRules: [...oldRules, rule] })
                );
              }
            }),
            switchMap(() => this.extractionRuleService.getById(id)),
            withLatestFrom(this.store.select(selectExtractionRules)),
            map(([rule, extractionRules]) => {
              const oldRules = (extractionRules ?? []).filter((r) => r.id !== rule.id);
              this.dexieService.addOrUpdateItems(TableNames.EXTRACTION_RULE, [...oldRules, rule]);
              return ExtractionRuleActions.loadExtractionRules({ extractionRules: [rule] });
            })
          )
        )
      )
    );

    this.createExtractionRule$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.createExtractionRule),
        switchMap(({ extractionRule }) => this.extractionRuleService.create(extractionRule)),
        switchMap((extractionRule) =>
          this.dexieService.add(TableNames.EXTRACTION_RULE, extractionRule).pipe(map(() => extractionRule))
        ),
        withLatestFrom(this.store.select(selectExtractionRules)),
        map(([newExtractionRule, extractionRules]) => {
          let currentExtractionRules = extractionRules ?? [];
          currentExtractionRules = [...currentExtractionRules, newExtractionRule];
          this.dexieService.addOrUpdateItems(TableNames.EXTRACTION_RULE, currentExtractionRules);
          return ExtractionRuleActions.loadExtractionRules({ extractionRules: [...currentExtractionRules] });
        })
      )
    );

    this.updateExtractionRule$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.updateExtractionRule),
        switchMap(({ extractionRule }) => this.extractionRuleService.update(extractionRule)),
        withLatestFrom(this.store.select(selectExtractionRules)),
        map(([updatedExtractionRule, extractionRules]) => {
          const currentExtractionRules = extractionRules ?? [];
          const index = currentExtractionRules.findIndex(
            (extractionRule) => extractionRule.id === updatedExtractionRule.id
          );
          if (index !== -1) {
            currentExtractionRules[index] = updatedExtractionRule;
          }
          this.dexieService.addOrUpdateItems(TableNames.EXTRACTION_RULE, currentExtractionRules);
          return ExtractionRuleActions.loadExtractionRules({ extractionRules: [...currentExtractionRules] });
        })
      )
    );

    this.deleteExtractionRule$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.deleteExtractionRule),
        concatMap(({ id }) =>
          this.extractionRuleService.delete(id).pipe(
            withLatestFrom(this.store.select(selectExtractionRules)),
            map(([, types]) => {
              const extractionRules = (types ?? []).filter((d) => d.id !== id);
              this.dexieService.addOrUpdateItems(TableNames.EXTRACTION_RULE, extractionRules);
              return ExtractionRuleActions.loadExtractionRules({ extractionRules });
            })
          )
        )
      )
    );
  }
}
